import { EmojiIcon } from '@mm/icons';
import classnames from 'classnames';
import React, { Suspense } from 'react';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import './EmojiPicker.scss';
import { componentDidMount, componentWillUnmount } from './lifecycle';

const EmojiMartPicker = React.lazy(
  () =>
    import(
      /* webpackChunkName: "EmojiMartPicker" */ // @ts-ignore
      './EmojiMartPicker'
    ),
);

const dropdownStyles: React.CSSProperties = {
  position: 'absolute',
  zIndex: 100,
  right: '2px',
  top: '28px',
};

type OuterProps = {
  onEmojiPicked: (emoji: string) => any;
  className?: string;
  classNameForIcon?: string;
  color?: string;
  perLine?: number;
  disabled?: boolean;
  additionalDropdownStyles?: React.CSSProperties;
};

type InnerProps = {
  dropdownOpen: boolean;
  setDropdownOpen: (open: boolean) => any;
  onRef: (ref: any) => void;
};

type Props = OuterProps & InnerProps;

const refHandlers = props => {
  let node: null | HTMLDivElement = null;
  return {
    onRef: () => ref => {
      node = ref;
    },
    handleOutsideClick:
      ({ restoreSelector, addInputLogic, onBlur }) =>
      e => {
        if (node && node.contains(e.target)) {
          return;
        }
        props.setDropdownOpen(false);
      },
  };
};

const EmojiPickerComponent: React.FC<Props> = props => {
  const {
    setDropdownOpen,
    dropdownOpen,
    onEmojiPicked,
    className,
    onRef,
    classNameForIcon,
    color,
    children,
    disabled,
    additionalDropdownStyles = { width: 'fit-content' },
  } = props;
  const perLine = props.perLine || 9;
  const onSelect = emoji => {
    onEmojiPicked(emoji.native);
    setDropdownOpen(false);
  };

  const classList = classnames('emoji-picker', className, {
    'emoji-picker--dropdown-open': dropdownOpen,
    'emoji-picker--disabled': disabled,
  });
  const iconClassList = classnames('emoji-picker__icon', classNameForIcon);

  const onIconClick = disabled
    ? undefined
    : () => setDropdownOpen(!dropdownOpen);

  return (
    <div className={classList} ref={onRef}>
      {!children && (
        <EmojiIcon
          className={iconClassList}
          color={color}
          onClick={disabled ? undefined : () => setDropdownOpen(!dropdownOpen)}
        />
      )}
      {children && <span onClick={onIconClick}>{children}</span>}

      {dropdownOpen && (
        <Suspense fallback={<div />}>
          <EmojiMartPicker
            style={{ ...dropdownStyles, ...additionalDropdownStyles }}
            title="Pick an Emoji"
            set="facebook"
            onSelect={onSelect}
            perLine={perLine}
          />
        </Suspense>
      )}
    </div>
  );
};

const enhance = compose<InnerProps, OuterProps>(
  withState('dropdownOpen', 'setDropdownOpen', false),
  withHandlers(refHandlers),
  lifecycle({ componentDidMount, componentWillUnmount }),
);

export const EmojiPicker = enhance(EmojiPickerComponent);
